import { ReactNode, useEffect, useMemo, useRef } from "react";
import type * as React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import SuperStepDot, { SuperStepState } from "./SuperStepDot";
import { SubStepType } from "./SubStep";
import { calculateStepState } from "./utils";
import VerticalStepItem from "./VerticalStepItem";

export interface Step {
  onClick?: (params: { index: number; isComplete: boolean }) => undefined;
  stepIndex: number;
  shortName: string;
  longName: string;
  subSteps: SubStepType[];
}

export type StepperLayout = "responsive" | "horizontal" | "vertical";

export type HorizontalStripSurface = "solid" | "transparent";

export interface StepperProps {
  activeStepIndex: number;
  greeting?: ReactNode;
  headerShown?: boolean;
  horizontalStripSurface?: HorizontalStripSurface;
  layout?: StepperLayout;
  showMobileGreeting?: boolean;
  steps: Step[];
}

export interface StepItemProps {
  menuItemIndex: number;
  stepIndex: number;
  label: string;
  activeStepIndex: number;
  subSteps?: SubStepType[];
}

const findActiveHorizontalStepIndex = (
  steps: Step[],
  activeStepIndex: number
): number => {
  const byState = steps.findIndex((step) => {
    const { stepState } = calculateStepState(step, activeStepIndex);
    return (
      stepState === SuperStepState.Active ||
      stepState === SuperStepState.ChildIsActive
    );
  });
  if (byState >= 0) {
    return byState;
  }

  // Fallback when indices fall between defined super-steps (e.g. kit survey pages)
  let closest = 0;
  steps.forEach((step, index) => {
    if (step.stepIndex <= activeStepIndex) {
      closest = index;
    }
  });
  return closest;
};

const Stepper: React.FC<StepperProps> = ({
  activeStepIndex,
  greeting,
  headerShown = false,
  horizontalStripSurface = "solid",
  layout = "responsive",
  showMobileGreeting = false,
  steps = []
}) => {
  const horizonalListItemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const horizontalScrollRef = useRef<HTMLUListElement | null>(null);
  const theme = useTheme();
  const screenWidthIsLgOrSmaller = useMediaQuery(theme.breakpoints.down("lg"));

  const isHorizontal = useMemo(
    () =>
      layout === "horizontal" ||
      (layout === "responsive" && screenWidthIsLgOrSmaller),
    [layout, screenWidthIsLgOrSmaller]
  );

  const renderVerticalSteps = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        position="relative"
      >
        <Box
          sx={{
            position: "absolute",
            top: 30,
            bottom: 60,
            left: "19px",
            borderLeft: `2px dashed ${COLORS.ACCENT2}`
          }}
        />
        {steps.map((step, index) => {
          return (
            <VerticalStepItem
              menuItemIndex={index}
              activeStepIndex={activeStepIndex}
              key={step.stepIndex}
              step={step}
            />
          );
        })}
      </Box>
    );
  };

  const renderFullVerticalStepper = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "30px",
          paddingLeft: "50px"
        }}
      >
        {!!greeting && greeting}
        {renderVerticalSteps()}
      </Box>
    );
  };

  const renderHorizontalStepper = () => {
    let barPosition: "fixed" | "relative" | "sticky" = "fixed";
    if (layout === "horizontal") {
      barPosition = "relative";
    } else if (headerShown) {
      barPosition = "sticky";
    }

    const stripTransparent = horizontalStripSurface === "transparent";

    return (
      <Box
        component="ul"
        ref={horizontalScrollRef}
        sx={{
          alignItems: "center",
          alignSelf: "flex-start",
          backgroundColor: stripTransparent ? "transparent" : COLORS.WHITE,
          boxShadow: stripTransparent ? "none" : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          boxSizing: "border-box",
          display: "flex",
          flexGrow: stripTransparent ? 0 : 1,
          gap: stripTransparent ? "30px" : "15px",
          height: stripTransparent ? "auto" : "50px",
          left: 0,
          margin: 0,
          maxHeight: stripTransparent ? "none" : "50px",
          minHeight: stripTransparent ? "auto" : undefined,
          msOverflowStyle: "none",
          overflowX: "scroll",
          overflowY: "hidden",
          padding: stripTransparent ? "8px 12px" : "15px 15px 15px 10px",
          position: barPosition,
          scrollbarWidth: "none",
          top: barPosition === "relative" ? undefined : 0,
          whiteSpace: "nowrap",
          width: stripTransparent ? "max-content" : "100%",
          zIndex: barPosition === "relative" ? undefined : 1000
        }}
      >
        {steps.map((step, index) => {
          const { stepState, superStepColoration } = calculateStepState(
            step,
            activeStepIndex
          );
          const overriddenStepState = [
            SuperStepState.ChildIsActive,
            SuperStepState.Active
          ].includes(stepState)
            ? SuperStepState.Active
            : stepState;
          const { onClick } = step;
          const isComplete = activeStepIndex > step.stepIndex;

          return (
            <Box
              component="li"
              key={step.stepIndex}
              onClick={() => onClick?.({ index: step.stepIndex, isComplete })}
              ref={(element: HTMLLIElement | null) => {
                horizonalListItemRefs.current[index] = element;
              }}
              sx={{
                alignItems: "center",
                cursor: onClick && isComplete ? "pointer" : "default",
                display: "flex",
                flexGrow: stripTransparent ? 0 : 1,
                flexDirection: "row",
                position: "relative",
                width: "max-content"
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center"
                }}
              >
                <SuperStepDot state={overriddenStepState} index={index + 1} />
                <Typography
                  sx={{
                    typography: {
                      xs: `body_strong_xs_${superStepColoration}`,
                      lg: `body_strong_${superStepColoration}`
                    }
                  }}
                >
                  {step.shortName}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const horizontalStepSignature = steps
    .map((step) => `${step.stepIndex}:${step.subSteps?.length ?? 0}`)
    .join("|");

  useEffect(() => {
    if (!isHorizontal) {
      return;
    }

    const container = horizontalScrollRef.current;
    if (!container) {
      return;
    }

    const activeListIndex = findActiveHorizontalStepIndex(
      steps,
      activeStepIndex
    );
    if (activeListIndex <= 0) {
      container.scrollTo({ left: 0, behavior: "auto" });
      return;
    }

    const item = horizonalListItemRefs.current[activeListIndex];
    if (!item) {
      return;
    }

    // scrollIntoView(inline: "start") overshoots in overflow strips and can
    // clip the step number. Scroll only this container, with a left inset.
    const LEFT_INSET_PX = 10;
    const delta =
      item.getBoundingClientRect().left -
      container.getBoundingClientRect().left -
      LEFT_INSET_PX;
    container.scrollTo({
      left: Math.max(0, container.scrollLeft + delta),
      behavior: "auto"
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- steps via signature
  }, [activeStepIndex, isHorizontal, horizontalStepSignature]);

  if (isHorizontal) {
    const putGreetingAfterBar =
      layout === "responsive" && screenWidthIsLgOrSmaller;
    const showGreetingInHorizontal =
      !!greeting &&
      (layout === "horizontal" ||
        (layout === "responsive" && showMobileGreeting));

    return (
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap:
            layout === "horizontal"
              ? { lg: (theme) => `calc(${theme.spacing(2)} + 15px)`, xs: "35px" }
              : { lg: 2, xs: "20px" },
          width: "100%"
        }}
      >
        {!putGreetingAfterBar && showGreetingInHorizontal ? greeting : null}
        {renderHorizontalStepper()}
        {putGreetingAfterBar && showGreetingInHorizontal ? greeting : null}
      </Box>
    );
  }

  return renderFullVerticalStepper();
};

export default Stepper;
